import { fileMatchesAccept, listedAcceptExtensions } from 'src/lib/file-accept';
import {
  describeReceivedFileType,
  type TranslateFn,
  type UploadFileRejection,
} from 'src/lib/pricelist-utils';

/** Toast copy when a file is outside the dropzone's `accept` list. */
export function unsupportedAcceptRejection(
  file: File,
  accept: string,
  gt: TranslateFn,
): UploadFileRejection {
  const extensions = listedAcceptExtensions(accept).join(', ');
  const hint = extensions
    ? gt('Please upload a file ending in {extensions}.', {
        context: 'toast message listing the extensions this dropzone accepts',
        extensions,
      })
    : gt('Please upload a file of an accepted type.', {
        context: 'toast message when a dropped file is not in the accept list',
      });
  return {
    kind: 'unsupported',
    title: gt('Unsupported file format', {
      context: 'toast title when an unsupported file type is selected',
    }),
    message: `${gt('Got a file of type', {
      context:
        'prefix before the type of an unsupported file the user tried to upload',
    })} “${describeReceivedFileType(file)}”. ${hint}`,
  };
}

/**
 * Apply the dropzone `accept` list and the caller's `validate` to each file.
 * Rejections are returned rather than thrown so a multi-file drop can keep the
 * valid ones.
 */
export async function acceptDroppedFiles(
  files: readonly File[],
  accept: string,
  validate: (file: File) => Promise<UploadFileRejection | null>,
  gt: TranslateFn,
): Promise<{ accepted: File[]; rejections: UploadFileRejection[] }> {
  const outcomes = await Promise.all(
    files.map(async (file) => {
      const rejection = fileMatchesAccept(file, accept)
        ? await validate(file)
        : unsupportedAcceptRejection(file, accept, gt);
      return { file, rejection };
    }),
  );
  const accepted: File[] = [];
  const rejections: UploadFileRejection[] = [];
  for (const { file, rejection } of outcomes) {
    if (rejection) {
      rejections.push(rejection);
    } else {
      accepted.push(file);
    }
  }
  return { accepted, rejections };
}
